import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
// icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
// utils and helpers
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../../redux/slices/modalSlice';
import { openSnackbar } from '../../../../redux/slices/common';
// components
import MDButton from 'components/MDButton';
import Search from 'examples/Search';
import StatusText from 'common/component/StatusText';
import CommonAgGridTable from "components/tables/CommonAgGridTable";
import { deleteSubRegion, getSubRegions } from "../../../../redux/slices/hyperwolf/settings";
import { debounce } from "utilities/common";

export default function SubRegions({ canView, canCreate, canEdit, canDelete }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTable, setRefreshTable] = useState(false);
  const [customFilters, setCustomFilters] = useState({});

  const columnDefs = [
    {
      headerName: 'Sub Region',
      field: 'name',
      key: 'name',
      suppressMenu: false,
      flex: 1,
      minWidth: 180,
      sortable: false
    },
    {
      headerName: 'Blaze Region',
      field: 'blazeRegionName',
      key: 'blazeRegionName',
      suppressMenu: false,
      flex: 1,
      minWidth: 180,
      sortable: false
    },
    {
      headerName: 'KML',
      field: 'kml',
      suppressMenu: false,
      sortable: false,
      flex: 2.5,
      minWidth: 300,
      cellRenderer: (params) => {
        if (!params?.data?.kmls || !params?.data?.kmls?.length) {
          return "-";
        }
        return (
          <Tooltip title={params?.data?.kml} sx={{ whiteSpace: 'nowrap' }} arrow placement="bottom">
            <span style={{ whiteSpace: 'normal', lineHeight: "normal" }}>{params?.data?.kmls?.find(kml => kml?.active)?.url}</span>
          </Tooltip>
        );
      },
      cellStyle: { "white-space": "normal" },
    },
    {
      headerName: 'Status',
      field: 'status',
      suppressMenu: false,
      flex: 0.75,
      maxWidth: 130,
      sortable: false,
      cellRenderer: (props) => {
        return <StatusText status={props?.data?.status ? "active" : "inactive"} />;
      },
    },
    {
      headerName: 'Actions',
      cellRenderer: (props) => {
        return <Box display='inline-flex'>
          <IconButton disabled={!canEdit} onClick={() => navigate(`/subregions/update/${props?.data._id}`)} color='primary' size='small' variant='primary'>
            <EditIcon width='16px' height='16px' />
          </IconButton>
          <IconButton
            disabled={!canDelete}
            color='error'
            size='small'
            variant='primary'
            sx={{ marginLeft: 1 }}
            onClick={() => dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: props.data?._id }, modal: 'DELETE_MODAL' }))}
          >
            <DeleteIcon width='16px' height='16px' />
          </IconButton>
        </Box>;
      },
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      maxWidth: 130,
      pinned: 'right'
    },

  ];

  const deleteHandler = async (regionId) => {
    try {
      dispatch(setModalLoader(true));

      const res = await dispatch(deleteSubRegion(regionId)).unwrap();
      setRefreshTable(prev => !prev)

      dispatch(openSnackbar({ content: res?.message, color: 'success', icon: 'check' }));
      dispatch(closeCustomModalDialog());
    } catch (err) {
      dispatch(setModalLoader(false));
      dispatch(openSnackbar({ content: err?.message, color: 'error', icon: 'warning' }));
    };
  };

  const searchHandler = useCallback(debounce((event) => {
    setCustomFilters({ ...customFilters, search: event.target.value });
    setRefreshTable(prev => !prev)
  }, 1000), []);

  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }} className="ag-grid-table-white">
      <Grid container alignItems='center' rowSpacing={2} columnSpacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Typography variant='h4'>Sub Regions</Typography>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Stack direction='row' justifyContent={{ xs: 'start', sm: 'end' }} flexWrap='wrap' alignItems='stretch' gap={2}>
            <Search placeholder='Search'
              label=''
              value={searchTerm}
              onChange={e => {
                const { value } = e.target;
                setSearchTerm(value);
                searchHandler(e)
              }}
              style={{ minWidth: "300px" }}
            />
            {
              canCreate &&
              <MDButton
                startIcon={<AddRoundedIcon fontSize='large' />}
                variant='contained' color='primary'
                onClick={() => navigate('/subregions/add')} >
                Add Sub Region
              </MDButton>
            }
          </Stack>
        </Grid>
      </Grid>

      <div className="ag-theme-quartz" style={{ height: "66vh", width: "100%" }}>
        <CommonAgGridTable
          pagination={false}
          columnDefs={columnDefs}
          getDataMethod={getSubRegions}
          refreshTable={refreshTable}
          customFilters={customFilters}
        />
      </div>
    </Box>
  );
}